import { useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { Portal } from '../../../components/Portal'

export const MealPicker = ({ date, slotName, recipes, currentSlot, onSave, onClear, onClose }) => {
  const [tab,        setTab]        = useState('recipe')
  const [search,     setSearch]     = useState('')
  const [customName, setCustomName] = useState(currentSlot?.custom_name ?? '')
  const [saving,     setSaving]     = useState(false)

  const filtered = (recipes ?? []).filter((r) =>
    r.title.toLowerCase().includes(search.toLowerCase()),
  )

  const handlePickRecipe = async (recipe) => {
    setSaving(true)
    try { await onSave({ date, slot_name: slotName, recipe_id: recipe.id, custom_name: null }) }
    finally { setSaving(false) }
  }

  const handleCustom = async (e) => {
    e.preventDefault()
    if (!customName.trim()) return
    setSaving(true)
    try { await onSave({ date, slot_name: slotName, recipe_id: null, custom_name: customName.trim() }) }
    finally { setSaving(false) }
  }

  const handleClear = async () => {
    setSaving(true)
    try { await onClear({ date, slot_name: slotName }) }
    finally { setSaving(false) }
  }

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[70dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
          <h3 className="font-bold text-text-primary capitalize">{slotName}</h3>
          <div className="flex gap-2">
            {currentSlot && (
              <button onClick={handleClear} className="text-xs text-rose-500 font-medium" disabled={saving}>
                Clear
              </button>
            )}
            <button onClick={onClose} className="text-text-muted p-1">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {[{ id: 'recipe', label: 'Recipe' }, { id: 'custom', label: 'Custom' }].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                tab === t.id ? 'text-primary border-b-2 border-primary' : 'text-text-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {tab === 'recipe' ? (
            <>
              <div className="px-4 pt-3 pb-2">
                <input
                  type="text"
                  placeholder="Search recipes…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              {filtered.length === 0 ? (
                <p className="text-center text-text-muted text-sm py-8">No recipes found</p>
              ) : (
                filtered.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => handlePickRecipe(r)}
                    disabled={saving}
                    className="w-full text-left px-5 py-3 hover:bg-background border-b border-border/40 last:border-0"
                  >
                    <div className="text-sm font-medium text-text-primary">{r.title}</div>
                    {r.cook_time_minutes && (
                      <div className="text-xs text-text-muted mt-0.5">{r.cook_time_minutes} min</div>
                    )}
                  </button>
                ))
              )}
            </>
          ) : (
            <form onSubmit={handleCustom} className="px-5 py-4 space-y-3">
              <input
                autoFocus
                type="text"
                placeholder="e.g. Takeout pizza"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Button type="submit" className="w-full" disabled={saving || !customName.trim()}>
                {saving ? <Spinner size="sm" /> : 'Save'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
    </Portal>
  )
}
